/**
 * Ambience checks (R35/D115) — the audio contract's loader half: every
 * reference resolves both directions, every condition is real, and dead
 * variants fail loudly.
 *
 * @remarks
 * The D89 treatment, audio-side:
 *
 * - References resolve BOTH directions: every ambience slot naming an asset
 *   finds it in the `audio` table, and every declared asset is named by some
 *   ambience slot (dead content otherwise — the D31/D47 law).
 * - `'silence'` is a VALUE, not a reference — it resolves by definition
 *   (Calder's §6.2: quiet on purpose), and it is exempt from the dead-content
 *   sweep because there is nothing to declare.
 * - Variant conditions join the condition walk (reference integrity) and the
 *   constant-condition check: a variant on a constant-false condition can
 *   never play; constant-true means it shadows the fallback permanently and
 *   the author should have written it AS the fallback.
 * - A variant whose value equals its declaration's fallback changes nothing
 *   when it fires — dead by construction, flagged as such.
 *
 * What this deliberately cannot check, named rather than papered: whether
 * the sound at `src` matches the room it plays under. Listening is the only
 * gate on that, exactly as human review is the only gate on a plate's paint.
 *
 * @packageDocumentation
 */

import type { AudioPlayback, ConditionalAmbience, ContentPackDefinition } from '@orpheus/content-schema';

import { findConstantBinaryStateCondition } from './authoring-reachability.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkCondition, declaredIdSetsOf } from './reference-walkers.js';
import { brokenReference, collectDuplicates } from './semantic-validation.js';
import { fieldOf, walkDocumentObjects } from './structural-walk.js';

/**
 * Run every ambience check against a schema-valid pack.
 *
 * @param contentPack - The parsed pack
 * @returns Every audio failure found (empty = the audio layer is sound)
 */
export const validateAudioAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const itemsById = new Map(contentPack.items.map((item) => [item.itemId, item]));
	const declared = declaredIdSetsOf(contentPack);
	const playbackKindById = new Map<string, AudioPlayback['kind']>(contentPack.audio.map((audio) => [audio.audioId, audio.playback.kind]));
	const referencedAudioIds = new Set<string>();

	/**
	 * One reference into the table: declared, of the kind its place plays (D264 — a bed loops under a room, a cue plays
	 * once with its line, and neither can stand in for the other), and counted as a use.
	 */
	const checkReference = (audioId: string, path: string, expectedKind: AudioPlayback['kind'], referrer: string): void => {
		referencedAudioIds.add(audioId);

		const kind = playbackKindById.get(audioId);

		if (kind === undefined) {
			failures.push(brokenReference(path, audioId, `${referrer} names an asset the audio table does not declare`));
		} else if (kind !== expectedKind) {
			failures.push(
				brokenReference(path, audioId, `${referrer} names a ${kind}, and only a ${expectedKind} plays there — a bed loops under a room, a cue plays once with its line`),
			);
		}
	};

	collectDuplicates(
		contentPack.audio.map((audio, audioIndex) => ({
			declarationPath: `audio[${String(audioIndex)}].audioId`,
			declaredId: audio.audioId,
		})),
		'audio asset',
		failures,
	);

	/** Walk one ambience declaration — the pack's or a room's. */
	const checkAmbience = (declaration: ConditionalAmbience, declarationPath: string): void => {
		const checkValue = (value: string, valuePath: string): void => {
			if (value !== 'silence') {
				checkReference(value, valuePath, 'stereoBed', 'the ambience');
			}
		};

		if (typeof declaration === 'string') {
			checkValue(declaration, declarationPath);

			return;
		}

		checkValue(declaration.fallback, `${declarationPath}.fallback`);

		declaration.variants.forEach((variant, variantIndex) => {
			const variantPath = `${declarationPath}.variants[${String(variantIndex)}]`;

			checkValue(variant.audio, `${variantPath}.audio`);
			checkCondition(variant.condition, `${variantPath}.condition`, declared, failures);

			const constantCondition = findConstantBinaryStateCondition(variant.condition, itemsById);

			if (constantCondition !== undefined) {
				failures.push(
					brokenReference(
						`${variantPath}.condition`,
						// `ambienceValueSchema` is `audioId | 'silence'` — both strings, so
						// the typeof guard that used to stand here could never be false.
						// Its `: 'silence'` arm was unreachable by construction: not a
						// defensive default but a branch nothing could enter (§2.1).
						variant.audio,
						constantCondition.type === 'switched_on'
							? 'that thing declares no switch, so this ambience variant can never play'
							: 'that thing declares no door, so this ambience variant always wins — it should BE the fallback',
					),
				);
			}

			if (variant.audio === declaration.fallback) {
				failures.push(
					brokenReference(
						`${variantPath}.audio`,
						variant.audio,
						'this variant chooses exactly its own fallback — firing it changes nothing, so the variant is dead',
					),
				);
			}
		});
	};

	if (contentPack.ambience !== undefined) {
		checkAmbience(contentPack.ambience, 'ambience');
	}

	// D132: the title theme is a reference into the same table — it must
	// resolve, and it COUNTS as a use, so a pack whose only bed is its theme
	// does not fail the dead-content sweep for keeping its title in music.
	const titleTheme = contentPack.manifest.presentation?.titleScreen?.theme;

	if (titleTheme !== undefined) {
		checkReference(titleTheme, 'manifest.presentation.titleScreen.theme', 'stereoBed', 'the title theme');
	}

	// D264: a line's cues, wherever a line is authored — walked structurally, because lines live in too many homes to
	// enumerate (the structural walk's own argument), and only a line carries `cues`.
	walkDocumentObjects(contentPack, (record, path) => {
		const cues = fieldOf(record, 'cues');

		if (Array.isArray(cues)) {
			(cues as readonly string[]).forEach((cue, cueIndex) => {
				checkReference(cue, `${path}.cues[${String(cueIndex)}]`, 'cue', 'the line');
			});
		}
	});

	contentPack.rooms.forEach((room, roomIndex) => {
		if (room.ambience !== undefined) {
			checkAmbience(room.ambience, `rooms[${String(roomIndex)}].ambience`);
		}
	});

	// The dead-content law, audio-side: a declared asset no ambience names.
	contentPack.audio.forEach((audio, audioIndex) => {
		if (!referencedAudioIds.has(audio.audioId)) {
			failures.push(
				brokenReference(
					`audio[${String(audioIndex)}].audioId`,
					audio.audioId,
					'no ambience, title theme or line names this asset, so it can never play',
				),
			);
		}
	});

	return failures;
};
